"use client";

import { cn } from "@newxsystems/shared";
import { useEffect, useLayoutEffect, useState } from "react";
import { BlockActions } from "../../../shared/components/BlockActions/BlockActions";
import { BlockCommonContent } from "../../../shared/components/BlockCommonContent/BlockCommonContent";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { TextMediaRegion } from "../TextMediaRegion/TextMediaRegion";
import type { ITextMediaLtrProps } from "./TextMediaLtr.props";

const desktopBreakpoint = "(min-width: 1024px)";
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

const useDesktopLayout = () => {
  const [isDesktop, setIsDesktop] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const query = window.matchMedia(desktopBreakpoint);
    const updateLayout = () => setIsDesktop(query.matches);

    updateLayout();
    query.addEventListener("change", updateLayout);

    return () => query.removeEventListener("change", updateLayout);
  }, []);

  return isDesktop;
};

export const TextMediaLtr = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  image,
  parentClass,
  spacing,
  video,
}: ITextMediaLtrProps) => {
  const isDesktop = useDesktopLayout();
  const hasCommonContent = Boolean(eyebrow || heading || description);
  const hasCopy = hasCommonContent || Boolean(buttons?.length);

  const copy = hasCopy ? (
    <div className="order-1 min-w-0 lg:order-2 lg:col-span-5" key="copy">
      <BlockCommonContent
        alignment={alignment}
        description={description}
        eyebrow={eyebrow}
        heading={heading}
        headingLevel="h2"
      />
      <BlockActions
        actions={buttons ?? []}
        alignment={alignment}
        className={hasCommonContent ? "mt-8" : undefined}
      />
    </div>
  ) : null;
  const media = (
    <TextMediaRegion
      className={cn("order-2 lg:order-1", hasCopy ? "lg:col-span-7" : "lg:col-span-12")}
      image={image}
      key="media"
      title={heading}
      video={video}
    />
  );

  return (
    <BlockContainer anchorId={anchorId} parentClass={parentClass} spacing={spacing}>
      <div className="grid min-w-0 gap-10 lg:grid-cols-12 lg:items-center lg:gap-6">
        {isDesktop ? media : copy}
        {isDesktop ? copy : media}
      </div>
    </BlockContainer>
  );
};

TextMediaLtr.displayName = "TextMedia::TextMediaLtr";
